'use client';

import { useStoresUI } from '@context/StoresContext';
import dynamic from 'next/dynamic';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import OutsideClickHandler from 'react-outside-click-handler';
import { useUI } from '@context/GlobalUIContext';
import { weeklyHours } from '@utils/store';
import { buildHoursMessaging } from '@utils/store/buildHoursMessaging';
import useScrollThrottled from '@hooks/useScrollThrottled';
import { useHasMounted } from '@hooks/useHasMounted/useHasMounted';
import { MapPinIcon } from '@phosphor-icons/react';
import {
  PromoBarLevels,
  trackPromoBarClick,
} from '_utils/GA4/trackPromoBarEvents';

import styles from './StoreSelector.module.scss';

const StoreFlyout = dynamic(
  () => import('@components/global/MyFavouriteStore/StoreFlyout/StoreFlyout'),
  {
    loading: () => null,
  }
);

export const StoreSelector = ({
  isPromoBar = false,
}: {
  isPromoBar?: boolean;
}) => {
  const t = useTranslationWithSelfFallback('common');
  const { favouriteStoreDetails } = useStoresUI();
  const { openStoresModal } = useUI();
  const [hoursShortMessage, setHoursShortMessage] = useState<null | string>(
    null
  );
  const [hoursLongMessage, setHoursLongMessage] = useState<null | string>(null);
  const [referenceElement, setReferenceElement] = useState<Element | null>(
    null
  );
  const [showStoreFlyout, setShowStoreFlyout] = useState(false);

  const handleScroll = useCallback(
    ({
      previousScrollPosition,
      currentScrollTop,
    }: {
      previousScrollPosition: number;
      currentScrollTop: number;
    }) => {
      const isScrolledDown = previousScrollPosition < currentScrollTop;
      if (isScrolledDown && showStoreFlyout) {
        setShowStoreFlyout(false);
      }
    },
    [showStoreFlyout]
  );

  useScrollThrottled(handleScroll);

  const storeText = favouriteStoreDetails
    ? `${favouriteStoreDetails.data.store_name} ${t(
        'header.storeInformation.store'
      )}, ${hoursShortMessage || ''}`
    : t('header.storeInformation.setMyStore');

  const setMyStoreClickHandler = () => {
    if (isPromoBar) {
      trackPromoBarClick({
        data: {
          level: PromoBarLevels.level_1,
          linkUrl: null,
          linkText: storeText,
          menuPath: storeText,
        },
      });
    }
    openStoresModal();
  };

  const toggleStoreFlyout = () => {
    if (isPromoBar) {
      trackPromoBarClick({
        data: {
          level: PromoBarLevels.level_1,
          linkUrl: null,
          linkText: storeText,
          menuPath: storeText,
        },
      });
    }
    setShowStoreFlyout((prevShowStoreFlyout) => !prevShowStoreFlyout);
  };

  useEffect(() => {
    if (!favouriteStoreDetails?.data) {
      setHoursShortMessage(null);
      setHoursLongMessage(null);
      return;
    }

    try {
      const userTime = new Date();
      const orderedWeek = weeklyHours(
        favouriteStoreDetails.data.store_hours,
        favouriteStoreDetails.data.store_hour_exceptions
      );

      if (orderedWeek) {
        const todaysHours = buildHoursMessaging(userTime, orderedWeek, t);
        if (todaysHours) {
          setHoursShortMessage(todaysHours.navigationMessage);
          setHoursLongMessage(todaysHours.fullMessage);
        } else {
          setHoursShortMessage(null);
          setHoursLongMessage(null);
        }
      } else {
        setHoursShortMessage(null);
        setHoursLongMessage(null);
      }
    } catch (error) {
      console.error('Error calculating store hours:', error);
      setHoursShortMessage(null);
      setHoursLongMessage(null);
    }
  }, [favouriteStoreDetails?.data, t]);

  const handleOutsideClick = useCallback(() => {
    setShowStoreFlyout(false);
  }, []);

  const hasMounted = useHasMounted();
  if (!hasMounted || favouriteStoreDetails === undefined) {
    return <Skeleton height={20} className={styles.skeleton} />;
  }

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <button
        type="button"
        className={styles.setMyStoreButton}
        onClick={
          !favouriteStoreDetails ? setMyStoreClickHandler : toggleStoreFlyout
        }
        ref={setReferenceElement}
        aria-label={storeText}
        aria-expanded={showStoreFlyout}
        aria-haspopup="dialog"
      >
        <MapPinIcon width={16} height={16} className={styles.mapPinIcon} />
        <span className={styles.storeName}>
          {favouriteStoreDetails && (
            <div className={styles.storeNameText}>
              {favouriteStoreDetails.data.store_name}{' '}
              {t('header.storeInformation.store')}
              <span className={styles.separator}>,</span>
            </div>
          )}
        </span>
        {favouriteStoreDetails ? (
          <span className={styles.storeHours}>{hoursShortMessage}</span>
        ) : (
          <span className={styles.setMyStoreText}>
            {t('header.storeInformation.setMyStore')}
          </span>
        )}
      </button>
      {showStoreFlyout && (
        <StoreFlyout
          showStoreFlyout={showStoreFlyout}
          storeDetails={favouriteStoreDetails}
          referenceElement={referenceElement}
          hoursLongMessage={hoursLongMessage}
          toggleStoreFlyout={toggleStoreFlyout}
          setShowStoreFlyout={setShowStoreFlyout}
        />
      )}
    </OutsideClickHandler>
  );
};

export default StoreSelector;
